import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import { getActiveCaisseYear, buildCaisseYearOptions } from '../utils/caisseActiveYear';
import { authJsonHeaders } from '../utils/authHeaders';
import { useAuth } from '../contexts/AuthContext';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import './NHPage.css';

export default function AllDepensesClientPage() {
  const { id } = useParams();
  const { activeCabinet } = useAuth();
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [depenses, setDepenses] = useState([]);
  const [error, setError] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const [filterType, setFilterType] = useState('mois');
  const [jourMode, setJourMode] = useState('date');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => getActiveCaisseYear());
  const years = buildCaisseYearOptions();

  useEffect(() => {
    if (id) {
      fetchClientInfo();
    } else {
      fetchAllClients();
    }
  }, [id, activeCabinet?.id]);

  const fetchClientInfo = async () => {
    try {
      const response = await fetch(`/api/clients/${id}`, { headers: authJsonHeaders() });
      const data = await response.json();
      if (data.success) setClientInfo(data.client);
    } catch (err) {
      console.error('Erreur client:', err);
    }
  };

  const fetchAllClients = async () => {
    try {
      const response = await fetch('/api/clients', { headers: authJsonHeaders() });
      const data = await response.json();
      if (data.success) setAllClients(data.clients || []);
    } catch (err) {
      console.error('Erreur clients:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  const formatMontant = (montant) => {
    const value = parseFloat(montant || 0);
    if (isNaN(value)) return '0,000 TND';
    return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} TND`;
  };

  const buildDepensesClientPrintUrl = (preview = false) => {
    const params = new URLSearchParams();
    const targetClientId = id || selectedClient?.id || null;
    params.set('filterType', filterType);
    params.set('jourMode', jourMode);
    if (targetClientId) params.set('client_id', targetClientId);
    if (filterType === 'jour' && jourMode === 'date' && selectedDate) params.set('date', selectedDate);
    if (filterType === 'jour' && jourMode === 'periode') {
      if (dateDebut) params.set('dateDebut', dateDebut);
      if (dateFin) params.set('dateFin', dateFin);
    }
    if (filterType === 'mois') {
      params.set('mois', String(selectedMonth));
      params.set('annee', String(selectedYear));
    }
    if (filterType === 'annee') params.set('annee', String(selectedYear));
    if (preview) params.set('preview', '1');
    return `/depenses-client/print?${params.toString()}`;
  };

  const handleSearchByFilters = async () => {
    try {
      setLoading(true);
      setError('');

      const targetClientId = id || selectedClient?.id || null;
      let url = '';
      const params = new URLSearchParams();

      if (targetClientId) {
        params.append('client_id', targetClientId);
        if (filterType === 'jour' && jourMode === 'date' && selectedDate) {
          params.append('date', selectedDate);
        } else if (filterType === 'jour' && jourMode === 'periode' && dateDebut && dateFin) {
          params.append('date_debut', dateDebut);
          params.append('date_fin', dateFin);
        } else if (filterType === 'mois') {
          params.append('mois', String(selectedMonth));
          params.append('annee', String(selectedYear));
        } else if (filterType === 'annee') {
          params.append('annee', String(selectedYear));
        }
        url = `/api/depenses/par-client?${params.toString()}`;
      } else {
        params.append('type', 'client');
        if (filterType === 'jour' && jourMode === 'date' && selectedDate) {
          params.append('date', selectedDate);
          url = `/api/depenses?${params.toString()}`;
        } else if (filterType === 'jour' && jourMode === 'periode' && dateDebut && dateFin) {
          params.append('date_debut', dateDebut);
          params.append('date_fin', dateFin);
          url = `/api/depenses/par-periode?${params.toString()}`;
        } else if (filterType === 'mois') {
          params.append('mois', String(selectedMonth));
          params.append('annee', String(selectedYear));
          url = `/api/depenses?${params.toString()}`;
        } else if (filterType === 'annee') {
          params.append('annee', String(selectedYear));
          url = `/api/depenses?${params.toString()}`;
        } else {
          url = `/api/depenses?${params.toString()}`;
        }
      }

      const response = await fetch(url, { headers: authJsonHeaders() });
      const data = await response.json();
      if (data.success) {
        // Uniquement les débits client (déjà filtrés côté API)
        setDepenses(data.depenses || []);
        setShowTable(true);
      } else {
        setError(data.error || 'Erreur lors du chargement des dépenses');
        setDepenses([]);
        setShowTable(true);
      }
    } catch (_) {
      setError('Erreur réseau lors du chargement des dépenses');
      setDepenses([]);
      setShowTable(true);
    } finally {
      setLoading(false);
    }
  };

  // Charger automatiquement les débits (mois / année courants)
  useEffect(() => {
    handleSearchByFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeCabinet?.id]);

  const total = depenses.reduce((s, d) => s + parseFloat(d.montant || 0), 0);

  return (
    <CgmModulePageLayout
      crumb="Comptabilité clients · Dépenses"
      title="Dépenses clients"
      subtitle="Opérations débit des clients"
      backTo="/"
      wide
    >
      {clientInfo && (
        <div className="alert alert-light border mb-3" role="status">
          <strong>{clientInfo.nom} {clientInfo.prenom}</strong>
          {clientInfo.telephone ? ` · ${clientInfo.telephone}` : ''}
          {clientInfo.email ? ` · ${clientInfo.email}` : ''}
        </div>
      )}

      <div className="cgm-list-toolbar">
        <div className="cgm-filter-panel cgm-period-panel">
          <div className="cgm-filter-panel__title">Filtrer les opérations débit (dépenses client)</div>
          <div className="cgm-period-body">
            <div className="cgm-period-tabs">
              <button type="button" className={`cgm-period-tab ${filterType === 'jour' ? 'active' : ''}`} onClick={() => setFilterType('jour')}>Jour</button>
              <button type="button" className={`cgm-period-tab ${filterType === 'mois' ? 'active' : ''}`} onClick={() => setFilterType('mois')}>Mois</button>
              <button type="button" className={`cgm-period-tab ${filterType === 'annee' ? 'active' : ''}`} onClick={() => setFilterType('annee')}>Année</button>
            </div>

            {!id && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Client</label>
                <select
                  className="hono-select-client"
                  value={selectedClient?.id || ''}
                  onChange={(e) => {
                    const clientId = e.target.value;
                    const client = allClients.find((c) => c.id === parseInt(clientId, 10));
                    setSelectedClient(client || null);
                  }}
                >
                  <option value="">Tous les clients</option>
                  {allClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nom} {client.prenom} ({client.username})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {filterType === 'jour' && (
              <div className="cgm-period-jour-panel">
                <div className="cgm-period-mode-group" role="group" aria-label="Mode jour">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="depJourDate" name="depJourMode" checked={jourMode === 'date'} onChange={() => setJourMode('date')} />
                    <label className="form-check-label" htmlFor="depJourDate">Par date</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" id="depJourPeriode" name="depJourMode" checked={jourMode === 'periode'} onChange={() => setJourMode('periode')} />
                    <label className="form-check-label" htmlFor="depJourPeriode">Par période</label>
                  </div>
                </div>
                <div className="cgm-period-filter-row">
                  {jourMode === 'date' ? (
                    <>
                      <label className="mb-0">Date</label>
                      <input type="date" className="hono-input-date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                    </>
                  ) : (
                    <>
                      <label className="mb-0">De</label>
                      <input type="date" className="hono-input-date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
                      <label className="mb-0">à</label>
                      <input type="date" className="hono-input-date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
                    </>
                  )}
                </div>
              </div>
            )}

            {filterType === 'mois' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Mois</label>
                <select className="hono-select-month" value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
                <label className="mb-0">Année</label>
                <select className="hono-select-year" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {filterType === 'annee' && (
              <div className="cgm-period-filter-row">
                <label className="mb-0">Année</label>
                <select className="hono-select-year" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="cgm-period-actions">
              <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--primary" onClick={handleSearchByFilters} disabled={loading}>
                {loading ? 'Recherche…' : 'Rechercher'}
              </button>
              <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--voir" onClick={() => window.open(buildDepensesClientPrintUrl(true), '_blank')} title="Visionner avant impression">
                Visionnage
              </button>
              <button type="button" className="cgm-toolbar-btn cgm-toolbar-btn--print" onClick={() => window.open(buildDepensesClientPrintUrl(), '_blank')} title="Imprimer la liste">
                Imprimer
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement…</span>
          </div>
        </div>
      )}

      {!loading && showTable && depenses.length > 0 && (
        <div className="nh-modern-table-card">
          <div className="nh-modern-table-toolbar">
            <div className="nh-modern-table-info">
              {depenses.length} dépense{depenses.length > 1 ? 's' : ''} · Total{' '}
              <strong>{formatMontant(total)}</strong>
            </div>
          </div>
          <div className="overview-table-container nh-modern-table-scroll">
            <table className="table overview-table nh-overview-data-table nh-modern-data-table">
              <thead>
                <tr>
                  <th><span className="nh-modern-th-inner">Date</span></th>
                  <th><span className="nh-modern-th-inner">Client</span></th>
                  <th><span className="nh-modern-th-inner">Libellé</span></th>
                  <th><span className="nh-modern-th-inner">Montant</span></th>
                </tr>
              </thead>
              <tbody>
                {depenses.map((depense, index) => {
                  const clientName = depense.client || depense.beneficiaire;
                  let libelleText = depense.libelle || depense.description || '—';
                  libelleText = libelleText
                    .replace(/^\[CGM PAYÉ\]\s*/i, '[PAYÉ PAR CGM] ')
                    .replace(/^\[CGM\]\s*/i, '[PAYÉ PAR CGM] ');
                  const rawText = (depense.libelle || depense.description || '').toUpperCase();
                  const isHonoraire =
                    rawText.includes('HONORAIRES REÇU') ||
                    rawText.includes('HONORAIRES RECU') ||
                    rawText.includes('AVANCE');
                  return (
                    <tr key={depense.id || index}>
                      <td>{formatDate(depense.date)}</td>
                      <td>{(clientName || '—').toString().toUpperCase()}</td>
                      <td>{String(libelleText).toUpperCase()}</td>
                      <td className="text-end">
                        <span className="nh-badge-amount" style={{ color: isHonoraire ? '#059669' : '#dc2626' }}>
                          {formatMontant(depense.montant)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}><strong>TOTAL</strong></td>
                  <td className="text-end"><strong>{formatMontant(total)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {!loading && showTable && depenses.length === 0 && !error && (
        <div className="text-center text-muted py-4">Aucun débit client pour ces critères.</div>
      )}

      {!loading && !showTable && (
        <div className="text-center text-muted py-4">
          Choisissez une période puis cliquez sur Rechercher.
        </div>
      )}
    </CgmModulePageLayout>
  );
}
